import { PARTNER_TRANSFERS_LIST } from 'adminConstants/routes';
import Page from 'components/new/Page';
import { PartnerTransferSection } from 'further-types/lp/partner-transfer';
import { CurrencyCode } from 'further-types/lp/vehicle';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { numberToCurrencyString } from 'further-ui/utils';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import api from 'lib/trpcClient';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { PartnerTransfersFormValues } from '../../hooks/usePartnerTransfersForm';
import PartnerTransferActionBar from '../../PartnerTransferActionBar';
import TransferLinkedCapitalActionCard from './TransferLinkedCapitalActionCard';

interface TransferCapitalActionsProps {
  vehicleId: string;
  partnerTransferId: string;
  baseCurrency: CurrencyCode;
  onNext: () => void;
  onPrevious: () => void;
  stepper: React.ReactNode;
}

const TransferCapitalActions: React.FC<TransferCapitalActionsProps> = ({
  vehicleId,
  partnerTransferId,
  baseCurrency,
  onNext,
  onPrevious,
  stepper,
}) => {
  const { getValues } = useFormContext<PartnerTransfersFormValues>();
  const { expandedRows, toggleRowExpansion } = useExpandedRows();

  const [isNavigating, setIsNavigating] = useState(false);
  const updatePartnerTransfer = api.partnerTransfers.update.useMutation();

  const [capitalActions] =
    api.partnerTransfers.getCapitalActions.useSuspenseQuery({
      partnerTransferId,
      vehicleId,
    });

  const rows = capitalActions.map((action) => ({
    ...action,
    id: action.id,
  }));

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'name',
      label: 'Name',
    },
    {
      key: 'ledgerDate',
      label: 'Date',
      render: (row) => row.ledgerDate.toReadable(),
    },
    {
      key: 'totalAmountLcy',
      label: 'Capital call/(distribution)',
      render: (row) =>
        numberToCurrencyString(row.totalToCallOrDistributeLcy, {
          fallback: '-',
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
  ];

  const handleNext = () => {
    setIsNavigating(true);
    const formValues = getValues();
    const input = {
      partnerTransferId,
      vehicleId,
      transferDate: formValues.transferDate,
      transferorCommitmentId: formValues.trasferrorCommitmentId,
      currentSection: PartnerTransferSection.CapitalActions,
      transferees: formValues.transferees.map((transferee) => ({
        investorId: transferee.investorId,
        amountLcy: transferee.amountLcy,
        percentage: transferee.percentage,
      })),
    };
    updatePartnerTransfer.mutate(input, {
      onSuccess: () => onNext(),
      onError: () => setIsNavigating(false),
    });
  };

  const handlePrevious = () => {
    onPrevious();
  };

  return (
    <Page
      goBackAction={PARTNER_TRANSFERS_LIST}
      spacing="lg"
      heading="Partner transfer"
      withHeadingDivider
      customActions={stepper}
      actionBar={
        <PartnerTransferActionBar
          onPrevious={handlePrevious}
          onNext={handleNext}
          isNextLoading={isNavigating}
          isNextDisabled={isNavigating}
          isPreviousDisabled={isNavigating}
        />
      }
    >
      <Table
        isExpandableRow={() => true}
        isExpandedRow={(row) => expandedRows.has(row.id)}
        onRowClick={(row) => toggleRowExpansion(row.id)}
        onRowExpand={(row) => toggleRowExpansion(row.id)}
        getExpandedContent={(row) => (
          <TransferLinkedCapitalActionCard
            capitalAction={row}
            baseCurrency={baseCurrency}
          />
        )}
        columns={columns}
        rows={rows}
        emptyMessage="No linked capital actions found for this partner transfer."
      />
    </Page>
  );
};

export default TransferCapitalActions;
